import { LitElement, html, css, unsafeHTML } from 'https://cdn.jsdelivr.net/gh/lit/dist@3/all/lit-all.min.js'

/**
 * Skio Universal Plan Picker
 * @docs https://integrate.skio.com/
 *
 * Uses LitJS (<5kb) https://lit.dev/ for reactive web components
 */

export class SkioPlanPicker extends LitElement {
  static properties = {
    key: { type: String },

    product: { type: Object },
    productHandle: { type: String },
    selectedVariant: { type: Object },

    options: { type: Object },

    moneyFormat: { type: String },

    quantity: { type: Number },
    rules: { type: Array },
    rule: { type: Object },
    cart: { type: Object },

    availableSellingPlanGroups: { state: true },
    selectedSellingPlanGroup: { type: Object },
    selectedSellingPlan: { type: Object },

    tooltipContent: { type: String },
    disableUrl: { type: Boolean },

    debug: { type: Boolean },
  }

  static styles = [
    css`
      :host {
        width: 100%;
        max-width: 440px;
      }

      .skio-plan-picker {
        display: flex;
        gap: 1rem;
        padding: 0;
        border: 0;
        font-size: 14px;
        color: black;
        width: 100%;
        margin-bottom: 1rem;
        font-family: 'OstiaAntica-Book' !important;
        font-weight: 300;
        margin-left: 0;
      }

      :host([layout='hidden']) .skio-plan-picker {
        display: none;
      }

      :host([layout='vertical']) .skio-plan-picker {
        flex-direction: column;
      }

      :host([layout='horizontal']) .skio-plan-picker {
        flex-direction: row;
        flex-wrap: wrap;
      }

      .sr-only {
        position: absolute;
        overflow: hidden;
        clip: rect(0, 0, 0, 0);
        width: 1px;
        height: 1px;
        margin: -1px;
        padding: 0;
        border: 0;
      }

      .group-container {
        display: block;
        position: relative;
        transition: border-color 0.2s ease;
        border: var(--skio-group-border-width, 1px) solid var(--skio-group-border-color, #ccc);
        border-radius: var(--skio-group-border-radius, 0);
        background-color: var(--skio-group-background-color, transparent);
        color: var(--skio-group-text-color, #000);
      }

      .group-container--selected {
        border-color: var(--skio-group-border-selected-color, #333);
        background-color: var(--skio-group-background-selected-color, transparent);
        color: var(--skio-group-text-selected-color, #000);
      }

      .group-container--last {
        order: 1;
      }

      :host([layout='horizontal']) .group-container {
        width: calc(50% - 0.5rem - calc(2 * var(--skio-group-border-width, 1px)));
      }

      .group-input {
        position: absolute;
        opacity: 0;
        width: 0px;
        height: 0px;
      }

      .group-input:focus-visible ~ .group-label {
        outline: 2px #ccc solid;
        outline-offset: 4px;
      }

      .group-label {
        display: flex;
        flex-direction: column;
        padding: 20px;
        cursor: pointer;
        position: relative;
      }

      .group-topline {
        display: flex;
        align-items: center;
        width: 100%;
        gap: 8px;
      }

      :host([layout='horizontal']) .group-topline {
        flex-direction: column;
        text-align: center;
      }

      .skio-radio__container {
        display: flex;
        color: var(--skio-group-border-color, #333);
      }

      .group-container--selected .skio-radio__container {
        color: var(--skio-group-border-selected-color, #333);
      }

      .skio-radio {
        transform-origin: center;
        opacity: 0;
        transform: scale(0);
        transition: transform 0.25s cubic-bezier(0.4, 0, 0.2, 1);
      }

      .group-label:hover .skio-radio {
        opacity: 0.75;
        transform: scale(1);
      }

      .group-container--selected .group-label .skio-radio {
        opacity: 1;
        transform: scale(1);
      }

      .skio-price {
        display: flex;
        justify-content: flex-end;
        flex-wrap: wrap;
        align-items: center;
        text-align: right;
        gap: 4px;
        line-height: 1;
        margin-left: auto;
        vertical-align: middle;
      }

      .skio-price s {
        font-size: 13px;
        opacity: 0.75;
      }

      :host([layout='horizontal']) .skio-price {
        text-align: center;
        margin-right: auto;
      }

      .group-content {
        display: flex;
        flex-direction: column;
        opacity: 1;
        width: auto;
        max-height: 1000px;
        transition:
          max-height 0.15s cubic-bezier(0.4, 0, 0.2, 1),
          opacity 0.25s cubic-bezier(0.4, 0, 0.2, 1);
      }

      .group-content.margin-left {
        margin-left: 25px;
      }

      .group-content p {
        margin: 0.5rem 0 0 0;
        font-size: 13px;
        color: #000;
      }

      :host([layout='horizontal']) .group-content {
        width: 100%;
        order: 2;
      }

      /* Hide frequency if not selected */
      .group-container:not(.group-container--selected) .group-content {
        pointer-events: none;
        opacity: 0;
        max-height: 0;
        visibility: hidden;
      }

      :host([layout='horizontal']) .group-container:not(.group-container--selected) + .group-content {
        pointer-events: none;
        opacity: 0;
        max-height: 0;
        visibility: hidden;
      }

      .group-title {
        width: 100%;
        max-width: 70%;
        line-height: 1.5;
      }

      :host([layout='horizontal']) .group-title {
        max-width: 100%;
      }

      .savings {
        color: var(--skio-discount-text-color, #fff);
      }

      .savings.bubble {
        padding: 10px 12px;
        background-color: var(--skio-discount-color, #0fa573);
        border: 1px var(--skio-discount-color, #0fa573) solid;
        border-radius: 50px;
        font-size: 12px;
        color: var(--skio-discount-text-color, #fff);
        white-space: nowrap;
        position: absolute;
        top: -18px;
        right: 13px;
        text-transform: uppercase;
        line-height: 12px;
      }

      .skio-tooltip {
        position: relative;
        display: inline-block;
        cursor: pointer;
        margin-left: 5px;
      }

      .skio-tooltip svg {
        vertical-align: middle;
      }

      .skio-tooltip-text {
        visibility: hidden;
        opacity: 0;
        position: absolute;
        top: 100%;
        left: 50%;
        transform: translateX(-50%);
        background-color: var(--skio-tooltip-background-color, #333);
        color: var(--skio-tooltip-text-color, #fff);
        padding: 5px 10px;
        border-radius: 5px;
        font-size: 12px;
        white-space: normal;
        width: 200px;
        z-index: 1000;
        transition: opacity 0.3s ease;
      }

      .skio-tooltip:hover .skio-tooltip-text,
      .skio-tooltip:focus-within .skio-tooltip-text {
        visibility: visible;
        opacity: 1;
      }

      .selling-plan-dropdown {
        appearance: none;
        -webkit-appearance: none;
        -moz-appearance: none;
        display: flex;
        align-items: center;
        gap: 5px;
        width: 100%;
        margin-top: 15px;
        padding: 8px 12px;
        background-color: #fff;
        background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' class='h-6 w-6' fill='none' viewBox='0 0 24 24' stroke='currentColor' stroke-width='2'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' d='M19 9l-7 7-7-7' /%3E%3C/svg%3E");
        background-repeat: no-repeat;
        background-position: right 10px top 50%;
        background-size: 16px;
        border: 1px solid #888888;
        border-radius: 30px;
        font-size: 14px;
        color: var(--skio-group-text-selected-color, #000);
        text-overflow: ellipsis;
        white-space: nowrap;
        outline: none !important;
        font-family: 'OstiaAntica-Book' !important;
      }

      .selling-plan-dropdown-label {
        color: #666666;
      }

      .selling-plan-dropdown--one {
        background-image: none;
        pointer-events: none;
        background: transparent;
      }

      .selling-plan-buttons {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(var(--skio-button-plan-selector-width, 20px), 1fr));
        gap: 10px;
        padding: 0.75rem 0;
        border: 0;
        font-size: 13px;
      }

      .selling-plan-buttons input[type='radio'] {
        position: absolute;
        opacity: 0;
        width: 0;
        height: 0;
      }

      .selling-plan-buttons label {
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        border: 1px solid #ccc;
        text-align: center;
        padding: 1rem 0.5rem;
      }

      .selling-plan-buttons input[type='radio']:checked + label {
        border-color: #000;
      }

      .selling-plan-buttons input:focus-visible + label {
        outline: 2px #ccc solid;
        outline-offset: 4px;
      }

      .group-content-additional {
        margin-top: 5px;
      }

      .group-content-additional ul {
        list-style: none;
        padding-left: 0;
      }
      .group-content-additional ul a {
        color: #121212;
      }

      .group-content-additional ul li {
        position: relative;
        padding-left: 1.75em;
        line-height: 25px;
      }

      .group-content-additional ul li::before {
        content: '';
        height: 1em;
        width: 1em;
        position: absolute;
        top: 0.25em;
        left: 0;
        mask-image: url(data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAyNCAyNCI+PHBhdGggZD0iTTEwLjkyMTUgMTQuNzExNUwxNS42MDc0IDEwLjA0NjVMMTQuODMzNCA5LjI3MjQ2TDEwLjkyMTUgMTMuMTYzNUw5LjE1MDcxIDExLjQxMzVMOC4zNzY2OSAxMi4xODc1TDEwLjkyMTUgMTQuNzExNVpNMTIuMDAxNSAxOS41ODMzQzEwLjk2MjEgMTkuNTgzMyA5Ljk4MTQ4IDE5LjM4NiA5LjA1OTcxIDE4Ljk5MTJDOC4xMzc5MiAxOC41OTY1IDcuMzMxMSAxOC4wNTM0IDYuNjM5MjUgMTcuMzYxOUM1Ljk0NzM5IDE2LjY3MDMgNS40MDQwMSAxNS44NjM5IDUuMDA5MSAxNC45NDI1QzQuNjE0MiAxNC4wMjEyIDQuNDE2NzUgMTMuMDQwOCA0LjQxNjc1IDEyLjAwMTRDNC40MTY3NSAxMC45NDgxIDQuNjE0MTEgOS45NjQwNiA1LjAwODgzIDkuMDQ5MjNDNS40MDM1NSA4LjEzNDM4IDUuOTQ2NjggNy4zMzEwNCA2LjYzODIxIDYuNjM5MTlDNy4zMjk3NSA1Ljk0NzMzIDguMTM2MiA1LjQwMzk0IDkuMDU3NTYgNS4wMDkwNEM5Ljk3ODkxIDQuNjE0MTQgMTAuOTU5MyA0LjQxNjY5IDExLjk5ODcgNC40MTY2OUMxMy4wNTE5IDQuNDE2NjkgMTQuMDM2IDQuNjE0MDUgMTQuOTUwOCA1LjAwODc3QzE1Ljg2NTcgNS40MDM0OSAxNi42NjkgNS45NDY2MiAxNy4zNjA5IDYuNjM4MTVDMTguMDUyNyA3LjMyOTY5IDE4LjU5NjEgOC4xMzI2NyAxOC45OTEgOS4wNDcwOEMxOS4zODU5IDkuOTYxNDkgMTkuNTgzNCAxMC45NDUzIDE5LjU4MzQgMTEuOTk4NkMxOS41ODM0IDEzLjAzOCAxOS4zODYgMTQuMDE4NiAxOC45OTEzIDE0Ljk0MDRDMTguNTk2NiAxNS44NjIxIDE4LjA1MzQgMTYuNjY5IDE3LjM2MTkgMTcuMzYwOEMxNi42NzA0IDE4LjA1MjcgMTUuODY3NCAxOC41OTYxIDE0Ljk1MyAxOC45OTFDMTQuMDM4NiAxOS4zODU5IDEzLjA1NDcgMTkuNTgzMyAxMi4wMDE1IDE5LjU4MzNaTTEyLjAwMDEgMTguNUMxMy44MDU2IDE4LjUgMTUuMzQwMyAxNy44NjgxIDE2LjYwNDIgMTYuNjA0MkMxNy44NjgxIDE1LjM0MDMgMTguNTAwMSAxMy44MDU2IDE4LjUwMDEgMTJDMTguNTAwMSAxMC4xOTQ0IDE3Ljg2ODEgOC42NTk3MiAxNi42MDQyIDcuMzk1ODNDMTUuMzQwMyA2LjEzMTk0IDEzLjgwNTYgNS41IDEyLjAwMDEgNS41QzEwLjE5NDUgNS41IDguNjU5NzggNi4xMzE5NCA3LjM5NTg5IDcuMzk1ODNDNi4xMzIgOC42NTk3MiA1LjUwMDA2IDEwLjE5NDQgNS41MDAwNiAxMkM1LjUwMDA2IDEzLjgwNTYgNi4xMzIgMTUuMzQwMyA3LjM5NTg5IDE2LjYwNDJDOC42NTk3OCAxNy44NjgxIDEwLjE5NDUgMTguNSAxMi4wMDAxIDE4LjVaIiBmaWxsPSJjdXJyZW50Q29sb3IiPjwvcGF0aD48L3N2Zz4K);
        mask-size: 140%;
        background-color: #121212;
        mask-position: calc(-0.25em + 1px) calc(-0.25em + 1px);
      }

      .skio-details {
        --text-color: #333;
        --text-color-secondary: #888;
        user-select: none;
        -webkit-user-select: none;
        margin-bottom: 20px;
        order: 3;
        max-width: max-content;
      }

      .skio-details summary::-webkit-details-marker,
      .skio-details summary::marker,
      .skio-details slot {
        color: rgba(0, 0, 0, 0) !important;
      }

      .skio-details summary {
        margin-top: 15px;
      }

      .skio-details summary span {
        font-size: 0.9em;
        display: flex;
        padding: 0.5em 0;
        cursor: pointer;
        align-items: center;
        gap: 10px;
        text-decoration: underline;
        margin-top: -40px;
        color: #000;
      }

      @keyframes fadeInDown {
        0% {
          opacity: 0;
          transform: translateY(-15px);
        }
        100% {
          opacity: 1;
          transform: translateY(0px);
        }
      }
      .skio-details[open] > .skio-details--content {
        animation-name: fadeInDown;
        animation-duration: 0.3s;
      }

      .skio-details--content {
        position: absolute;
        z-index: 1020;
        padding: 1em;
        width: fit-content;
        border-radius: 5px;
        background: white;
        box-shadow:
          0 0 5px rgb(23 24 24 / 5%),
          0 1px 2px rgb(0 0 0 / 7%);
      }

      .skio-details ul {
        margin: 0;
        padding: 0;
      }

      .skio-details ul li {
        display: flex;
        align-items: flex-start;
        gap: 0.75em;
        margin-bottom: 1em;
      }

      .skio-details .skio-content {
        display: flex;
        flex-direction: column;
      }

      .skio-details .skio-content p {
        font-size: 0.9em;
        margin-top: 0;
        margin-bottom: 0;
        letter-spacing: 0;
        line-height: 1.5;
        color: var(--text-color);
      }

      .skio-details ul li small {
        font-size: 0.7em;
        color: var(--text-color-secondary);
      }

      .skio-details .skio-icon {
        display: flex;
        width: 2.25em;
        height: 2.25em;
        color: var(--text-color);
        background: #f8f8f8;
        border-radius: 100%;
        flex-shrink: 0;
        align-items: center;
        justify-content: center;
      }

      .skio-details .skio-icon svg {
        width: 1.25em;
        height: 1.25em;
        color: inherit;
      }

      .skio-details--footer {
        display: flex;
        align-items: center;
        justify-content: space-between;
        font-size: 0.9em;
      }

      .skio-details--footer a {
        color: var(--text-color);
      }

      .skio-manage-link {
        text-decoration: underline;
      }

      .powered-by-skio {
        font-size: 0.8em;

        display: flex;
        text-decoration: none;

        align-items: center;
        gap: 3px;
      }

      .setupMode {
        position: relative;
        color: black;
        padding: 1rem;
        display: flex;
        flex-direction: column;
        gap: 10px;
        margin-bottom: 1rem;
        font-size: 14px;
        border-style: solid;
        border-width: 4px;
        border-image: repeating-linear-gradient(-55deg, #000, #000 20px, #ffb101 20px, #ffb101 40px) 10;
      }

      .setupMode:after {
        content: 'Setup Mode';
        position: absolute;
        top: 0;
        right: 0;
        background-color: black;
        color: white;
        padding: 0.25rem 1rem;
        font-size: 13px;
      }

      .setupMode .detection {
        display: grid;
        grid-template-columns: auto 1fr;
        gap: 10px;
      }

      .setupMode .detection small {
        grid-column: 1/-1;
      }
    `,

    /* ====================================================================
       EUREKA redesign — appended last so these rules win on source order.
       Visual only. No existing rule above is removed; we override values.

       V2 UPDATE — active / inactive card treatment.
       When a card is NOT selected the whole block mutes to grey (text,
       toggle, dropdown, benefit rows, images, savings bubble). When it IS
       selected everything returns to full brand colour. Driven entirely by
       the existing .group-container--selected class, so no extra JS state.
       ==================================================================== */
    css`
      /* ---- Shared tokens --------------------------------------------- */
      :host {
        --ep-ink: #121212;
        --ep-ink-muted: #B5B5B5;
        --ep-ink-muted-strong: #9A9A9A;
        --ep-orange: #FAAE50;
        --ep-orange-deep: #D8962E;
        --ep-line: #E3DED3;
        --ep-line-muted: #E5E5E5;
        --ep-strike-red: #E03A2F;

        /* ============================================================
           >>> INACTIVE THUMBNAIL TREATMENT <
           How much colour the benefit-card product images keep when the
           Autoship row is toggled OFF.

             --ep-thumb-grayscale : 0 = full colour, 1 = fully grey
             --ep-thumb-opacity   : 0 = invisible,   1 = fully opaque

           0.55 / 0.65 = clearly de-emphasised but the product colours
           still read through (matches the reference mock). Lower the
           grayscale value for more colour, raise it for less.
           ============================================================ */
        --ep-thumb-grayscale: 0.55;
        --ep-thumb-opacity: 0.65;
      }

      .skio-plan-picker {
        gap: 0;
      }

      /* ---- Option containers ----------------------------------------- */
      .group-container {
        border-width: 1.5px;
        border-color: var(--ep-line-muted);
        border-radius: 12px;
        margin-bottom: 10px;
        overflow: visible;
        background: #fff !important;
        transition:
          border-color 0.22s ease,
          background-color 0.22s ease;
      }

      /* Selected emphasis */
      .group-container.group-container--selected {
        border-color: var(--ep-ink);
        background: #F9F9F9FF !important;
      }

      /* Keep the subscription content expanded even when NOT selected.
         Picking "Buy One Time" should only mute the colours + flip the
         toggle, never collapse the frequency dropdown / benefit card.
         This overrides the base collapse rule. */
      .group-container:not(.group-container--selected) .group-content {
        opacity: 1;
        max-height: 1000px;
        visibility: visible;
        pointer-events: auto;
      }

      .group-label {
        padding: 16px 16px 14px;
      }
      .group-topline {
        gap: 14px;
      }
      .group-title {
        max-width: none;
        display: flex;
        align-items: center;
        gap: 8px;
        font-size: 17px;
        font-weight: 500;
        line-height: 1.3;
        color: var(--ep-ink);
        transition: color 0.22s ease;
      }
      .group-container:not(.group-container--selected) .group-title {
        color: var(--ep-ink-muted-strong);
      }

      /* ---- Toggle switch (visual stand-in for the radio) -------------- */
      .skio-toggle {
        display: inline-flex;
        flex-shrink: 0;
      }
      .skio-toggle__track {
        position: relative;
        display: inline-block;
        width: 46px;
        height: 24px;
        border-radius: 50px;
        background: #E8E8E8;
        border: 1px solid #E8E8E8;
        transition:
          background 0.25s ease,
          border-color 0.25s ease;
      }
      .skio-toggle__knob {
        position: absolute;
        top: 50%;
        left: 3px;
        transform: translateY(-50%);
        width: 20px;
        height: 20px;
        border-radius: 50%;
        background: #B5B5B5;
        transition:
          left 0.25s cubic-bezier(0.4, 0, 0.2, 1),
          background 0.25s ease;
      }
      /* ON state — orange track, solid black knob (matches mock) */
      .group-container--selected .skio-toggle__track {
        background: var(--ep-orange);
        border-color: var(--ep-ink);
      }
      .group-container--selected .skio-toggle__knob {
        left: calc(100% - 20px - 2px);
        background: var(--ep-ink);
      }

      /* ---- Price ----------------------------------------------------- */
      .skio-price {
        font-size: 17px;
        font-weight: 500;
        gap: 6px;
        color: var(--ep-ink);
        transition: color 0.22s ease;
      }
      .group-container:not(.group-container--selected) .skio-price {
        color: var(--ep-ink-muted-strong);
      }
      .skio-price s {
        font-size: 14px;
        color: #6F695D;
        opacity: 1;
      }
      .group-container:not(.group-container--selected) .skio-price s {
        color: var(--ep-ink-muted);
      }

      /* ---- Discount badge ("Save $X") --------------------------------- */
      .savings.bubble {
        text-transform: none;
        background-color: var(--ep-orange);
        border-color: var(--ep-orange);
        color: var(--ep-ink);
        font-size: 15px;
        font-weight: 500;
        padding: 8px 16px;
        top: -16px;
        right: 14px;
        transition:
          background-color 0.22s ease,
          border-color 0.22s ease,
          color 0.22s ease;
      }
      /* Muted badge when the subscription row is off */
      .group-container:not(.group-container--selected) .savings.bubble {
        background-color: #9A9A9A;
        border-color: #9A9A9A;
        color: #fff;
      }

      /* ---- Frequency dropdown ----------------------------------------- */
      /* Base (inactive) — flat white pill, grey outline, grey text */
      .selling-plan-dropdown {
        margin-top: 12px;
        padding: 8px 18px;
        height: auto;
        border-radius: 50px;
        font-size: 15px;
        font-weight: 500;
        color: var(--ep-ink-muted-strong);
        background-color: #fff;
        border: 1px solid #DCDCDC;
        background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' fill='none' viewBox='0 0 24 24' stroke='%239A9A9A' stroke-width='2'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' d='M19 9l-7 7-7-7'/%3E%3C/svg%3E");
        background-repeat: no-repeat;
        background-position: right 16px top 50%;
        background-size: 16px;
        transition:
          background-color 0.22s ease,
          border-color 0.22s ease,
          color 0.22s ease;
      }

      /* Active — orange filled pill, black border, black text + caret */
      .group-container--selected .selling-plan-dropdown {
        color: var(--ep-ink);
        border: 1px solid var(--ep-ink);
        background-color: var(--ep-orange);
        background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' fill='none' viewBox='0 0 24 24' stroke='%23121212' stroke-width='2'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' d='M19 9l-7 7-7-7'/%3E%3C/svg%3E");
        background-repeat: no-repeat;
        background-position: right 16px top 50%;
        background-size: 16px;
      }

      .selling-plan-dropdown--one {
        background-image: none;
      }
      .group-container--selected .selling-plan-dropdown--one {
        background-image: none;
      }

      .selling-plan-dropdown-label {
        display: none;
      }

      @media (max-width: 480px) {
        .group-title {
          font-size: 16px;
        }
        .skio-price {
          font-size: 16px;
        }
      }
    `,

    /* ====================================================================
       EUREKA benefit card (hardcoded) — styles for the .ep-sub-* classes
       baked into additionalContentText(). Lives inside the shadow DOM.

       V2: adds the muted/inactive variants. Everything keys off the
       ancestor .group-container--selected class.

       NOTE: .ep-popup* styles are NOT here. The popups get moved to
       document.body when opened, which puts them outside this shadow DOM.
       Those styles are injected into document.head once in
       connectedCallback() via injectPopupStyles().
       ==================================================================== */
    css`
      /* ---- Benefit card shell ---------------------------------------- */
      .group-content-additional {
        margin-top: 14px;
      }

      .ep-sub-card {
        background: #FFFFFF;
        border: 1px solid #ffffff;
        border-radius: 6px;
        padding: 0 16px;
        margin-bottom: 2px;
        transition: border-color 0.22s ease;
      }
      .group-container--selected .ep-sub-card {
        border-color: #FFFFFF;
      }

      /* Header line: "Commitment Free: modify and cancel, anytime." */
      .ep-sub-cardhead {
        display: block;
        text-align: center;
        font-size: 14px;
        line-height: 1.4;
        color: var(--ep-ink);
        padding: 16px 0 12px;
        transition: color 0.22s ease;
      }
      .group-container:not(.group-container--selected) .ep-sub-cardhead {
        color: var(--ep-ink-muted-strong);
      }

      /* ---- Benefit rows ---------------------------------------------- */
      .ep-sub-row {
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 12px 0;
        border-top: 1px solid #AFAEAEFF;
      }

      .ep-sub-row-welcome-t {
        padding-top : 12px;
        border-top: 1px solid #AFAEAEFF;
      }

      .ep-sub-card--onetime .ep-sub-row-welcome-t {
        padding-top: 12px;
        border-top: 1px solid #AFAEAEFF;
      }

      /* ================================================================
         PRODUCT THUMBNAILS
         Selected  → full colour, fully opaque.
         Inactive  → PARTIALLY desaturated only, so the product colours
                     still show through (see --ep-thumb-* tokens at the
                     top of the previous style block). Previously this was
                     grayscale(1) / opacity .45, which killed the colour
                     entirely.
         ================================================================ */
      .ep-sub-thumb {
        width: 46px;
        height: 46px;
        border-radius: 8px;
        object-fit: contain;
        flex-shrink: 0;
        filter: grayscale(var(--ep-thumb-grayscale, 0.55));
        opacity: var(--ep-thumb-opacity, 0.65);
        transition:
          filter 0.22s ease,
          opacity 0.22s ease;
      }
      .group-container--selected .ep-sub-thumb {
        filter: none;
        opacity: 1;
      }

      .ep-sub-name {
        flex: 1;
        display: flex;
        align-items: center;
        gap: 6px;
        font-size: 15px;
        letter-spacing: 0;
        line-height: 1.3;
        color: var(--ep-ink-muted-strong);
        transition: color 0.22s ease;
      }
      .group-container--selected .ep-sub-name {
        color: var(--ep-ink);
        font-weight: 500;
      }

      /* ---- Info icon -------------------------------------------------- */
      .ep-sub-info {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 17px;
        height: 17px;
        flex-shrink: 0;
        box-sizing: border-box;
        border: none;
        border-radius: 50%;
        background: transparent;
        color: #B5B5B5;
        cursor: pointer;
        outline: none;
        padding: 0;
        transition: color 0.22s ease;
      }
      .group-container--selected .ep-sub-info {
        color: #8A8A8A;
      }
      .ep-sub-info:focus-visible {
        outline: 2px solid var(--ep-orange);
        outline-offset: 2px;
      }
      .ep-sub-info svg {
        display: block;
        width: 17px;
        height: 17px;
        fill: none;
        stroke: currentColor;
        pointer-events: none;
      }

      /* ---- Benefit price (strikethrough + FREE) ----------------------- */
      .ep-sub-price {
        display: flex;
        align-items: baseline;
        gap: 8px;
        font-size: 15px;
        font-weight: 500;
        color: var(--ep-ink-muted-strong);
        white-space: nowrap;
        transition: color 0.22s ease;
      }
      .group-container--selected .ep-sub-price {
        color: var(--ep-ink);
      }

      /* Inactive strike = grey. Active strike = red (per mock). */
      .ep-sub-price s {
        font-family: OstiaAntica-Light;
        font-weight: 400;
        font-size: 15px;
        color: var(--ep-ink-muted);
        text-decoration-color: var(--ep-ink-muted);
        transition:
          color 0.22s ease,
          text-decoration-color 0.22s ease;
      }
      .group-container--selected .ep-sub-price s {
        color: var(--ep-strike-red);
        text-decoration-color: var(--ep-strike-red);
      }

      .ep-sub-price .ep-sub-free {
        text-transform: uppercase;
        letter-spacing: 0.01em;
      }

      /* ---- Guarantee box (optional — hidden by default in V2) ---------- */
      .ep-sub-guarantee {
        display: none;
        align-items: center;
        gap: 12px;
        background: #FFFFFF;
        border-radius: 10px;
        padding: 0;
        margin-top: 10px;
        margin-bottom: 4px;
        color: var(--ep-ink);
        border: 1px solid #E5E5E5;
      }
      .ep-sub-guarantee--show {
        display: flex;
      }
      .ep_sb_guar {
        width: 22px;
        background: #F2F2F2;
        padding: 12px 16px;
        border-top-left-radius: 10px;
        border-bottom-left-radius: 10px;
        filter: grayscale(var(--ep-thumb-grayscale, 0.55));
        opacity: var(--ep-thumb-opacity, 0.65);
        transition:
          filter 0.22s ease,
          opacity 0.22s ease;
      }
      .group-container--selected .ep_sb_guar {
        filter: none;
        opacity: 1;
      }
      .ep-sub-guarantee__text {
        display: flex;
        flex-direction: column;
        gap: 2px;
        line-height: 1.3;
        padding-right: 12px;
      }
      .ep-sub-guarantee__title {
        font-size: 13px;
        font-weight: 500;
        color: var(--ep-ink-muted-strong);
        letter-spacing: 0;
        transition: color 0.22s ease;
      }
      .group-container--selected .ep-sub-guarantee__title {
        color: var(--ep-ink);
      }
      .ep-sub-guarantee__sub {
        font-size: 11px;
        color: var(--ep-ink-muted);
        letter-spacing: 0;
        font-family: OstiaAntica-Light;
        transition: color 0.22s ease;
      }
      .group-container--selected .ep-sub-guarantee__sub {
        color: #121212;
      }

      /* Popups: hidden while they live inside this shadow DOM. Once they
         move to document.body, the document-level CSS injected from
         connectedCallback() takes over. */
      .ep-popup {
        display: none;
      }

      @media (max-width: 480px) {
        .ep-sub-cardhead {
          font-size: 13px;
        }
        .ep-sub-name,
        .ep-sub-price,
        .ep-sub-price s {
          font-size: 14px;
        }
        .ep-sub-thumb {
          width: 42px;
          height: 42px;
        }
        .ep-sub-guarantee__title {
          font-size: 12.5px;
        }
        .ep-sub-guarantee__sub {
          font-size: 11px;
        }
      }

      
    `,

    /* ====================================================================
       EUREKA — INDEPENDENT PADDING + BORDER-RADIUS PER CARD.  (CSS ONLY)

       No markup change. Both selectors below key off classes that already
       exist in render():

         .group-container--last                 → the BUY ONE TIME card
              (Skio adds this class whenever options.onetime_first is false,
               which is the default and what puts Autoship on top)

         .group-container:not(.group-container--last)  → the AUTOSHIP card(s)

       Both selectors are more specific than the plain `.group-container` /
       `.group-label` rules above, and this block is last in the array, so
       these values win on both specificity and source order.

       NOTE: if a merchant ever turns ON "onetime_first" in the theme editor,
       the --last class moves to the Autoship card and the two rule sets swap
       meaning. Just swap the two selectors if that happens.
       ==================================================================== */
    css`
      :host {
        /* ============================================================
           AUTOSHIP CARD
           ============================================================ */
        --ep-sub-radius: 12px;
        --ep-sub-pad-top: 16px;
        --ep-sub-pad-right: 16px;
        --ep-sub-pad-bottom: 14px;
        --ep-sub-pad-left: 16px;
        --ep-sub-gap-bottom: 10px;

        /* ============================================================
           BUY ONE TIME CARD  (independent — edit these on their own)
           ============================================================ */
        --ep-ot-radius: 12px;
        --ep-ot-pad-top: 16px;
        --ep-ot-pad-right: 16px;
        --ep-ot-pad-bottom: 16px;
        --ep-ot-pad-left: 16px;
        --ep-ot-gap-bottom: 10px;
      }

      /* ---- AUTOSHIP card --------------------------------------------- */
      .group-container:not(.group-container--last) {
        border-radius: var(--ep-sub-radius);
        margin-bottom: var(--ep-sub-gap-bottom);
      }
      .group-container:not(.group-container--last) > .group-label {
        padding: 24px 16px 16px;
      }

      /* ---- BUY ONE TIME card ----------------------------------------- */
      .group-container.group-container--last {
        border-radius: var(--ep-ot-radius);
        margin-bottom: var(--ep-ot-gap-bottom);
      }
      .group-container.group-container--last > .group-label {
        padding: 10px 16px;
      }

      @media (max-width: 375px) {
        .group-container:not(.group-container--last) > .group-label {
          padding: 24px 10px 10px;
        }
        .ep-sub-card {
          padding: 0px 10px;
        }
        .ep-sub-name, .ep-sub-price, .ep-sub-price s {
          font-size: 12px;
        }
        .ep-sub-cardhead {
          font-size: 12px;
        }
      }
    `,
  ]

  constructor() {
    super()

    // Start - Debug Variables
    this.debug = this.debug || window?.Shopify?.designMode
    this.variantChanged = false
    // End - Debug Variables

    this.rules = []
    this.rule = null
    this.quantity = 1
    this.showDetailsHover = false
  }

  async connectedCallback() {
    super.connectedCallback()

    // Inject popup CSS into document.head once (popups get moved to <body>
    // on open, so their styles must live outside this shadow DOM).
    SkioPlanPicker.injectPopupStyles()

    this.log('Mounted')

    if (this.productHandle) {
      this.loading = true
      this.fetchProduct(this.productHandle)
    }

    this.bindFormEvents()
    this.bindCartEvents()

    this.rules = await this.fetchRules()
    await this.getRule()
  }

  // Injects the .ep-popup* styles into <head> exactly once per page.
  // Idempotent — checks for an existing #ep-popup-styles tag before adding.
  static injectPopupStyles() {
    if (typeof document === 'undefined') return
    if (document.getElementById('ep-popup-styles')) return

    const style = document.createElement('style')
    style.id = 'ep-popup-styles'
    style.textContent = `
      .ep-popup {
        display: none;
        position: fixed;
        inset: 0;
        background: rgba(0, 0, 0, 0.55);
        z-index: 9999999999999999999999999999999999;
        align-items: center;
        justify-content: center;
        padding: 20px;
        box-sizing: border-box;
        font-family: 'OstiaAntica-Book', system-ui, -apple-system, sans-serif;
      }
      .ep-popup * { box-sizing: border-box; }

      .ep-popup__dialog {
        background: #FFFFFF;
        border-radius: 14px;
        max-width: 860px;
        width: 100%;
        max-height: 90vh;
        overflow: hidden;
        position: relative;
        display: flex;
        flex-direction: row;
        box-shadow: 0 20px 60px rgba(0, 0, 0, 0.25);
      }

      .ep-popup__close {
        position: absolute;
        top: 14px;
        right: 14px;
        background: rgba(255, 255, 255, 0.92);
        border: none;
        width: 36px;
        height: 36px;
        border-radius: 50%;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 2;
        color: #121212;
        padding: 0;
        transition: background 0.2s ease;
      }
      .ep-popup__close:hover {
        background: #FFFFFF;
      }

      .ep-popup__img {
        width: 50%;
        max-width: 430px;
        object-fit: cover;
        display: block;
        flex-shrink: 0;
      }

      .ep-popup__content {
        padding: 44px 36px 36px;
        flex: 1;
        overflow-y: auto;
        color: #121212;
      }

      .ep-popup__eyebrow {
        font-family: Windsor;
        color: #FAAE50FF !important;
        font-size: 24px !important;
        font-weight: 400 !important;
        line-height: 26px !important;
        margin-bottom: 12px !important;
      }

      .ep-popup__title {
        color: #121212;
        font-size: 32px !important;
        font-style: normal;
        font-weight: 600 !important;
        letter-spacing: 0 !important;
        margin-bottom: 15px !important;
        font-family: 'OstiaAntica-Book';
        line-height: 40px !important;
      }

      .ep-popup__text {
        font-size: 15px;
        line-height: 1.55;
        color: #333333;
        margin-bottom: 14px;
      }

      .ep-popup__body {
        font-size: 15px;
        line-height: 1.55;
        color: #333333;
      }
      .ep-popup__body p {
        font-size: 16px !important;
        line-height: 24px !important;
        margin: 0 0 12px 0 !important;
      }
      .ep-popup__body p:last-child {
        margin-bottom: 0;
      }

      .ep-popup__tc {
        font-size: 12px;
        color: #6F695D;
        margin-top: 24px;
        line-height: 1.5;
      }
      .ep-popup__tc a {
        color: #6F695D;
        text-decoration: underline;
      }

      @media (max-width: 700px) {
        .ep-popup__dialog {
          flex-direction: column;
          max-height: 92vh;
        }
        .ep-popup__img {
          width: 100%;
          max-width: 100%;
          height: 220px;
        }
        .ep-popup__content {
          padding: 28px 22px 24px;
        }
        .ep-popup__title {
          font-size: 24px;
        }
      }
    `
    document.head.appendChild(style)
  }

  // SECTION: Element Templates
  radioTemplate() {
    return html`
      <div class="skio-toggle" aria-hidden="true">
        <span class="skio-toggle__track">
          <span class="skio-toggle__knob"></span>
        </span>
      </div>
    `
  }

  sellingPlanDropdown(group) {
    return html`
      <select
        class="selling-plan-dropdown ${this.getAvailableSellingPlans(group).length == 1 ? 'selling-plan-dropdown--one' : ''}"
        @change=${e => {
          this.selectSellingPlan(e.target.value)
        }}
      >
        ${group
          ? this.getAvailableSellingPlans(group).map(
              selling_plan => html` <option ?selected=${group.selected_selling_plan.id === selling_plan.id} value=${selling_plan.id}>${selling_plan.name}</option> `
            )
          : ''}
      </select>
    `
  }

  sellingPlanButtons(group) {
    return html`
      <fieldset class="selling-plan-buttons">
        <legend class="sr-only">Select subscription interval</legend>
        ${group
          ? this.getAvailableSellingPlans(group).map(
              selling_plan => html`
                <input
                  type="radio"
                  name="selling_plan_button"
                  value="${selling_plan.id}"
                  id="selling_plan_button-${selling_plan.id}"
                  @change="${e => this.selectSellingPlan(selling_plan.id)}"
                  ?checked=${group.selected_selling_plan === selling_plan}
                />

                <label for="selling_plan_button-${selling_plan.id}"> ${selling_plan.name} </label>
              `
            )
          : ''}
      </fieldset>
    `
  }

  invalidIcon() {
    return html`
      <svg width="24" height="24" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="#D22B2B" class="w-6 h-6">
        <path stroke-linecap="round" stroke-linejoin="round" d="M9.75 9.75l4.5 4.5m0-4.5l-4.5 4.5M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    `
  }

  validIcon() {
    return html`
      <svg width="24" height="24" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="#0BDA51" class="w-6 h-6">
        <path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    `
  }

  setupMode() {
    return html`
      <div class="setupMode">
        Skio Plan Picker
        <small>This will not appear on the live site.</small>

        <small>Key: ${this.key}</small>

        <div class="detection">${this.product ? this.validIcon() : this.invalidIcon()} Product</div>

        <div class="detection">${this.selectedVariant ? this.validIcon() : this.invalidIcon()} Selected Variant</div>

        <div class="detection">${this.form ? this.validIcon() : this.invalidIcon()} Form</div>

        <div class="detection">${this.variantInput ? this.validIcon() : this.invalidIcon()} Variant Input</div>

        <div class="detection">
          ${this.variantChanged ? this.validIcon() : this.invalidIcon()} Variant Change Detection
          <small>Please attempt to change the selected variant.</small>
        </div>

        <small>If any of these don't pass please troubleshoot using our <a target="_blank" rel="noreferrer" href="https://integrate.skio.com/troubleshooting">troubleshooting guide</a>.</small>
      </div>
    `
  }

  groupContent(group) {
    return html`
      <div class="group-content">
        <span class="selling-plan-dropdown-label">${this.additionalFrequencyLabel()}</span>
        ${this.options?.selector_type == 'dropdown' ? this.sellingPlanDropdown(group) : ''}

        <!-- TODO: Make the label have the border instead of the group -->
        ${this.options?.selector_type == 'button' ? this.sellingPlanButtons(group) : ''} ${this.additionalContentText()}
      </div>
    `
  }

  showDetails() {
    return html`
      <details class="skio-details" @mouseover=${e => this.detailsMouseover()} @mouseleave=${e => this.detailsMouseleave()}>
        <summary>
          <span>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              class="ai ai-ArrowRepeat"
            >
              <path d="M18 2l3 3-3 3" />
              <path d="M6 22l-3-3 3-3" />
              <path d="M21 5H10a7 7 0 0 0-7 7" />
              <path d="M3 19h11a7 7 0 0 0 7-7" />
            </svg>

            How do subscriptions work?
          </span>
        </summary>
        <div class="skio-details--content">
          <ul>
            <li>
              <div class="skio-icon">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z"
                  />
                </svg>
              </div>
              <div class="skio-content">
                <p>Get exclusive deals</p>
                <small>Subscribe for unique discounts</small>
              </div>
            </li>
            <li>
              <div class="skio-icon">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                </svg>
              </div>
              <div class="skio-content">
                <p>Edit your subscription anytime</p>
                <small>Edit products, delivery schedule and more</small>
              </div>
            </li>
            <li>
              <div class="skio-icon">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path
                    fill-rule="evenodd"
                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                    clip-rule="evenodd"
                  />
                </svg>
              </div>

              <div class="skio-content">
                <p>No commitment</p>
                <small>Easy to cancel if it's not for you</small>
              </div>
            </li>
          </ul>
          <div class="skio-details--footer">
            <a class="skio-manage-link" href="/a/account/login">Manage subscriptions</a>

            <a style="letter-spacing: 0" class="powered-by-skio" href="https://skio.com/?utm_source=eonsincshop.myshopify.com&utm_medium=details_popover" target="_blank" rel="noopener">
              Powered by
              <svg width="24" height="11" viewBox="0 0 24 11" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M4.28399 5.78801C4.12399 5.63601 3.93599 5.50801 3.71999 5.40401C3.50399 5.30001 3.27599 5.24801 3.03599 5.24801C2.85199 5.24801 2.67999 5.28401 2.51999 5.35601C2.36799 5.42801 2.29199 5.55201 2.29199 5.72801C2.29199 5.89601 2.37599 6.01601 2.54399 6.08801C2.71999 6.16001 2.99999 6.24001 3.38399 6.32801C3.60799 6.37601 3.83199 6.44401 4.05599 6.53201C4.28799 6.62001 4.49599 6.73601 4.67999 6.88001C4.86399 7.02401 5.01199 7.20001 5.12399 7.40801C5.23599 7.61601 5.29199 7.86401 5.29199 8.15201C5.29199 8.52801 5.21599 8.84801 5.06399 9.11201C4.91199 9.36801 4.71199 9.57601 4.46399 9.73601C4.22399 9.89601 3.95199 10.012 3.64799 10.084C3.34399 10.156 3.03999 10.192 2.73599 10.192C2.24799 10.192 1.76799 10.116 1.29599 9.96401C0.831989 9.80401 0.443989 9.57201 0.131989 9.26801L1.23599 8.10401C1.41199 8.29601 1.62799 8.45601 1.88399 8.58401C2.13999 8.71201 2.41199 8.77601 2.69999 8.77601C2.85999 8.77601 3.01599 8.74001 3.16799 8.66801C3.32799 8.58801 3.40799 8.45201 3.40799 8.26001C3.40799 8.07601 3.31199 7.94001 3.11999 7.85201C2.92799 7.76401 2.62799 7.67201 2.21999 7.57601C2.01199 7.52801 1.80399 7.46401 1.59599 7.38401C1.38799 7.30401 1.19999 7.19601 1.03199 7.06001C0.871989 6.92401 0.739989 6.75601 0.635989 6.55601C0.531989 6.35601 0.479989 6.11601 0.479989 5.83601C0.479989 5.47601 0.555989 5.17201 0.707989 4.92401C0.859989 4.66801 1.05599 4.46001 1.29599 4.30001C1.53599 4.14001 1.79999 4.02401 2.08799 3.95201C2.38399 3.87201 2.67599 3.83201 2.96399 3.83201C3.41199 3.83201 3.84799 3.90401 4.27199 4.04801C4.70399 4.18401 5.06799 4.39201 5.36399 4.67201L4.28399 5.78801Z"
                  fill="black"
                />
                <path d="M12.8481 10H10.4121L8.45615 7.13201H8.42015V10H6.44015V0.928009H8.42015V6.44801H8.45615L10.3641 4.02401H12.7521L10.4481 6.72401L12.8481 10Z" fill="black" />
                <path
                  d="M15.7009 2.11601C15.7009 2.26801 15.6689 2.41201 15.6049 2.54801C15.5489 2.67601 15.4689 2.78801 15.3649 2.88401C15.2689 2.98001 15.1489 3.05601 15.0049 3.11201C14.8689 3.16801 14.7249 3.19601 14.5729 3.19601C14.2529 3.19601 13.9849 3.09201 13.7689 2.88401C13.5529 2.66801 13.4449 2.41201 13.4449 2.11601C13.4449 1.97201 13.4729 1.83601 13.5289 1.70801C13.5849 1.57201 13.6649 1.45601 13.7689 1.36001C13.8729 1.26401 13.9929 1.18801 14.1289 1.13201C14.2649 1.06801 14.4129 1.03601 14.5729 1.03601C14.7249 1.03601 14.8689 1.06401 15.0049 1.12001C15.1489 1.17601 15.2689 1.25201 15.3649 1.34801C15.4689 1.44401 15.5489 1.56001 15.6049 1.69601C15.6689 1.82401 15.7009 1.96401 15.7009 2.11601ZM13.5889 10V4.02401H15.5569V10H13.5889Z"
                  fill="black"
                />
                <path
                  d="M23.4516 6.98801C23.4516 7.47601 23.3636 7.92001 23.1876 8.32001C23.0116 8.71201 22.7716 9.04801 22.4676 9.32801C22.1636 9.60001 21.8116 9.81201 21.4116 9.96401C21.0116 10.116 20.5836 10.192 20.1276 10.192C19.6796 10.192 19.2516 10.116 18.8436 9.96401C18.4436 9.81201 18.0916 9.60001 17.7876 9.32801C17.4916 9.04801 17.2556 8.71201 17.0796 8.32001C16.9036 7.92001 16.8156 7.47601 16.8156 6.98801C16.8156 6.50001 16.9036 6.06001 17.0796 5.66801C17.2556 5.27601 17.4916 4.94401 17.7876 4.67201C18.0916 4.40001 18.4436 4.19201 18.8436 4.04801C19.2516 3.90401 19.6796 3.83201 20.1276 3.83201C20.5836 3.83201 21.0116 3.90401 21.4116 4.04801C21.8116 4.19201 22.1636 4.40001 22.4676 4.67201C22.7716 4.94401 23.0116 5.27601 23.1876 5.66801C23.3636 6.06001 23.4516 6.50001 23.4516 6.98801ZM21.5556 6.98801C21.5556 6.79601 21.5236 6.60801 21.4596 6.42401C21.3956 6.24001 21.3036 6.08001 21.1836 5.94401C21.0636 5.80001 20.9156 5.68401 20.7396 5.59601C20.5636 5.50801 20.3596 5.46401 20.1276 5.46401C19.8956 5.46401 19.6916 5.50801 19.5156 5.59601C19.3396 5.68401 19.1916 5.80001 19.0716 5.94401C18.9596 6.08001 18.8716 6.24001 18.8076 6.42401C18.7516 6.60801 18.7236 6.79601 18.7236 6.98801C18.7236 7.18001 18.7516 7.36801 18.8076 7.55201C18.8716 7.73601 18.9636 7.90401 19.0836 8.05601C19.2036 8.20001 19.3516 8.31601 19.5276 8.40401C19.7036 8.49201 19.9076 8.53601 20.1396 8.53601C20.3716 8.53601 20.5756 8.49201 20.7516 8.40401C20.9276 8.31601 21.0756 8.20001 21.1956 8.05601C21.3156 7.90401 21.4036 7.73601 21.4596 7.55201C21.5236 7.36801 21.5556 7.18001 21.5556 6.98801Z"
                  fill="black"
                />
              </svg>
            </a>
          </div>
        </div>
      </details>
    `
  }

  // !SECTION: Element Templates

  render() {
    if (!this.viable() || this.options?.layout === 'hidden') {
      return this.debug ? this.setupMode() : ''
    }

    if (this.selectedVariant.selling_plan_allocations.length === 0 && !this.options?.show_without_subscription) {
      return this.debug ? this.setupMode() : ''
    }

    if (this.availableSellingPlanGroups?.length == 0 && !this.options?.show_without_subscription) return

    return html`
      ${this.debug ? this.setupMode() : null}

      <fieldset class="skio-plan-picker" role="radiogroup" aria-labelledby="skio-plan-picker-legend">
        <legend id="skio-plan-picker-legend" class="${!this.options?.show_legend ? 'sr-only' : ''}">${unsafeHTML(this.options?.legend_content)}</legend>

        ${!this.product.requires_selling_plan
          ? html`
              <div
                class="group-container ${this.selectedSellingPlanGroup == null ? 'group-container--selected' : ''} ${!this.options?.onetime_first ? 'group-container--last' : ''}"
                @click=${e => this.handleContainerClick(e, null)}
              >
                <input
                  id="one-time-${this.key}"
                  class="group-input"
                  name="skio-group-${this.key}"
                  type="radio"
                  value="One time purchase"
                  ?checked=${!this.selectedSellingPlanGroup}
                  @change=${() => this.selectSellingPlanGroup(null)}
                />

                <label class="group-label" for="one-time-${this.key}">
                  <div class="group-topline">
                    ${this.options?.layout == 'horizontal' ? '' : this.options?.show_radio_selector ? this.radioTemplate() : ''}

                    <!-- EUREKA: hardcoded copy. Change the string below to relabel. -->
                    <div class="group-title">${this.options?.ep_onetime_title || 'Buy One Time'}</div>

                    <div class="skio-price" aria-live="polite">
                      ${this.options?.show_compare_price && this.selectedVariant?.compare_at_price > this.selectedVariant.price
                        ? html`<s aria-hidden="true">${this.money(this.selectedVariant.compare_at_price)}</s>`
                        : ''}
                      ${this.money(this.selectedVariant.price)} ${this.options?.show_unit_price ? this.unitPriceTemplate() : ''}
                    </div>
                  </div>
                </label>
              </div>
            `
          : ''}
        ${this.availableSellingPlanGroups?.length
          ? this.availableSellingPlanGroups.map(
              (group, index) => html`
                <div class="group-container ${this.isGroupSelected(group) ? 'group-container--selected' : ''}" @click=${e => this.handleContainerClick(e, group)}>
                  <input
                    id="group-${index}-${this.key}"
                    class="group-input"
                    name="skio-group-${this.key}"
                    type="radio"
                    value="${group.id}"
                    @change=${() => this.selectSellingPlanGroup(group)}
                    ?checked=${this.isGroupSelected(group)}
                  />

                  <label class="group-label" for="group-${index}-${this.key}">
                    <div class="group-topline">
                      ${this.options?.layout == 'horizontal' ? '' : this.options?.show_radio_selector ? this.radioTemplate() : ''}

                      <div class="group-title">
                        <!-- EUREKA: hardcoded copy. "Autoship" replaces the old
                             options.subscription_title ("Subscribe & Save").
                             Prepaid groups still use their own title/name. -->
                        ${group.name === 'Prepaid' ? this.options?.prepaid_title || group.name : (this.options?.ep_autoship_title || 'Autoship')}
                        ${this.discountText(group.selected_selling_plan)
                          ? html` <span class="savings ${this.options?.discount_style}"> ${this.discountText(group.selected_selling_plan)} </span> `
                          : html``}
                        ${this.tooltipContent
                          ? html`<span class="skio-tooltip" tabindex="0" aria-describedby="skio-tooltip-${this.key}-${index}">
                              <svg width="16" height="16" fill="currentColor" viewBox="0 0 16 16"><path d="M8 1a7 7 0 1 0 0 14A7 7 0 0 0 8 1zM7 4h2v2H7V4zm0 3h2v5H7V7z" /></svg>
                              <span id="skio-tooltip-${this.key}-${index}" class="skio-tooltip-text" role="tooltip">${this.tooltipContent}</span>
                            </span>`
                          : ''}
                      </div>

                      <div class="skio-price" aria-live="polite">
                        ${this.options?.show_compare_price &&
                        (this.selectedVariant?.compare_at_price > this.selectedVariant.price || this.selectedVariant.price > this.price(group.selected_selling_plan, false))
                          ? html`<s aria-hidden="true">${this.money(this.selectedVariant.compare_at_price ? this.selectedVariant.compare_at_price : this.selectedVariant.price)}</s>`
                          : ''}
                        ${this.price(group.selected_selling_plan)} ${this.options?.show_unit_price ? this.unitPriceTemplate(group.selected_selling_plan) : ''}
                      </div>
                    </div>

                    ${this.options?.layout == 'vertical' && this.options?.dropdownPosition == 'inside' ? this.groupContent(group) : ''}
                  </label>
                </div>
                ${this.isGroupSelected(group) ? (this.options?.layout == 'horizontal' || this.options?.dropdownPosition == 'underneath' ? this.groupContent(group) : '') : ''}
              `
            )
          : ''}
        ${this.options?.show_details == true ? this.showDetails() : ''}
      </fieldset>
    `
  }

  async fetchRules() {
    if (sessionStorage.getItem('skio-discount-rules')) {
      return JSON.parse(sessionStorage.getItem('skio-discount-rules'))
    } else {
      const rules = await fetch(`https://api.skio.com/storefront-http/get-rules-by-domain-or-hostname?domain=${window?.Shopify?.shop}`)
        .then(response => response.json())
        .then(response => {
          return response.rules.filter(rule => rule.type !== 'surpriseDelight').sort((a, b) => a.minQuantityToDiscount - b.minQuantityToDiscount)
        })

      sessionStorage.setItem('skio-discount-rules', JSON.stringify(rules))
      return rules
    }
  }

  eligibleItemCount(rule) {
    const eligibleQuantity = rule?.productVariantIds.some(gid => gid.includes(this.selectedVariant.id)) ? this.quantity : 0

    if (!this.cart?.items) return eligibleQuantity

    const cartItems = Number(
      this.cart?.items.reduce(
        (aggregate, item) =>
          rule?.productVariantIds.some(gid => gid.includes(item.id) && item.selling_plan_allocation && gid.includes(this.selectedVariant.id)) ? aggregate + item.quantity : aggregate,
        0
      )
    )

    return cartItems + eligibleQuantity
  }

  getRule() {
    let largestMinQuantity = {
      value: -1,
      index: -1,
    }

    this.rules.forEach((rule, index) => {
      if (rule.code && largestMinQuantity.value < rule.minQuantityToDiscount) {
        if (rule.minQuantityToDiscount <= this.eligibleItemCount(rule)) {
          largestMinQuantity.value = rule.minQuantityToDiscount
          largestMinQuantity.index = index
        }
      }
    })

    this.rule = largestMinQuantity.index > -1 ? this.rules[largestMinQuantity.index] : null
  }

  bindCartEvents() {
    document.addEventListener('CartUpdated', event => {
      this.cart = event.detail
    })
  }

  bindFormEvents() {
    // First try to find form by specified form_id
    this.form = document.querySelector(`#${this.options?.form_id}`)

    // If no form found by ID, search through parents and page
    if (!this.form) {
      this.form = this.findProductForm()
    }

    if (!this.form) {
      this.error('No valid product form found')
      return
    }

    // Add debug styling if debug mode is enabled
    if (this.debug) {
      this.form.style.borderStyle = 'solid'
      this.form.style.borderWidth = '4px'
      this.form.style.borderImage = 'repeating-linear-gradient(-55deg,#000,#000 20px,#ffb101 20px,#ffb101 40px)10'
    }

    // Ensure form has an ID
    if (!this.form.getAttribute('id')) {
      this.form.setAttribute('id', `skio-form-${this.product.id}`)
    }

    // Set form attribute on selling plan input
    const sellingPlanInput = this.querySelector('input[name="selling_plan"]')
    if (sellingPlanInput) {
      sellingPlanInput.setAttribute('form', this.form.getAttribute('id'))
    }

    this.variantInput = this.form.querySelector('[name="id"]')
    if (!this.variantInput) this.variantInput = document.querySelector(`[name="id"][form="${this.options?.form_id}"]`)

    if (this.variantInput) {
      this.variantInput.addEventListener('change', e => {
        this.selectedVariant = this.product.variants.find(variant => variant.id == e.target.value)
        this.variantChanged = true
      })
    }

    const $quantityInput = this.form.querySelector('[name="quantity"]')

    if ($quantityInput) {
      this.quantity = Number($quantityInput.value)
      $quantityInput.addEventListener('input', () => {
        this.quantity = Number($quantityInput.value)
      })
    }

    let skio = this
    document.addEventListener('variantChanged', function (e) {
      let variantId = e.detail.variantId
      let variant = skio.product.variants.find(x => x.id == variantId)
      if (variant) skio.selectedVariant = variant
      else skio.log('Unable to find variant with id: ', variantId)
      this.variantChanged = true
      skio.requestUpdate()
    })
  }

  findProductForm() {
    // First try finding form through parent elements
    const formFromParent = this.findFormThroughParents()
    if (formFromParent) return formFromParent

    // If no form found through parents, look for any valid product forms on page
    const allProductForms = this.getAllProductForms()
    return allProductForms[0] || null
  }

  findFormThroughParents() {
    let currentElement = this
    let depth = 0
    const maxDepth = 5

    while (currentElement.parentElement && depth < maxDepth) {
      const forms = Array.from(currentElement.querySelectorAll('form'))
      const validForm = forms.find(form => this.isValidProductForm(form))

      if (validForm) return validForm

      currentElement = currentElement.parentElement
      depth++
    }

    return null
  }

  getAllProductForms() {
    return Array.from(document.forms).filter(form => {
      const hasVariantId = this.getVariantId(form)
      return this.isValidProductForm(form) && hasVariantId
    })
  }

  isValidProductForm(form) {
    const hasCartAction = form.getAttribute('action')?.includes('/cart/add')
    const hasSubmitButton = !!form.querySelector('button, [type="submit"]')
    return hasCartAction && hasSubmitButton
  }

  getVariantId(form) {
    // Try getting ID from FormData first
    const formData = new FormData(form)
    const idFromFormData = formData.get('id')
    if (idFromFormData) return Number(idFromFormData)

    // Fallback to finding input element
    const idInput = form.querySelector('[name="id"]')
    return idInput ? Number(idInput.value) : null
  }

  detailsMouseover() {
    let details = this.renderRoot.querySelector('.skio-details')
    let summary = this.renderRoot.querySelector('.skio-details summary')
    if (!details.hasAttribute('open') && this.showDetailsHover == false) {
      summary.click()
      this.showDetailsHover = true
    }
  }

  detailsMouseleave() {
    let details = this.renderRoot.querySelector('.skio-details')
    let summary = this.renderRoot.querySelector('.skio-details summary')
    if (details.hasAttribute('open') && this.showDetailsHover == true) {
      summary.click()
      this.showDetailsHover = false
    }
  }

  updated(changed) {
    if (!this.viable()) return

    if (changed.has('product') && this.product) {
      this.key = this.key ? this.key : this.product.id
    }

    if (changed.has('selectedVariant') && this.selectedVariant) {
      const filteredSellingPlanGroups = this.product.selling_plan_groups.filter(
        selling_plan_group =>
          selling_plan_group.app_id === 'SKIO' && !selling_plan_group.name.toLowerCase().includes('hidden-') && !selling_plan_group.name.toLowerCase().includes('dynamic box subscription')
      )

      //update availableSellingPlanGroups based on skioSellingPlanGroups and selectedVariant.id
      this.availableSellingPlanGroups = filteredSellingPlanGroups.filter(selling_plan_group =>
        selling_plan_group.selling_plans.some(selling_plan =>
          this.selectedVariant.selling_plan_allocations.some(selling_plan_allocation => selling_plan_allocation.selling_plan_id === selling_plan.id)
        )
      )

      if (this.options?.combine_groups) {
        this.availableSellingPlanGroups = [
          {
            name: this.options?.combined_group_name,
            selling_plans: this.availableSellingPlanGroups.flatMap(group => group.selling_plans),
            id: 'combined_group',
            app_id: 'SKIO',
          },
        ]
      }

      //update selectedSellingPlan value
      if (this.availableSellingPlanGroups?.length) {
        const url = new URL(window.location.href)
        const urlSelectedSellingPlanId = url.searchParams.get('selling_plan')

        //update each group with a default selected_selling_plan
        this.availableSellingPlanGroups.forEach(group => {
          const availableSellingPlans = this.getAvailableSellingPlans(group)

          const urlSelectedSellingPlan = availableSellingPlans.find(plan => plan.id == urlSelectedSellingPlanId)
          const nameSelectedSellingPlan = availableSellingPlans.find(plan => plan.name === this.selectedSellingPlan?.name)
          const defaultSellingPlan =
            this.options?.default_subscription && this.options.default_subscription.trim() !== ''
              ? availableSellingPlans.find(plan => plan.name.toLowerCase().includes(this.options.default_subscription.toLowerCase()))
              : null

          group.selected_selling_plan = urlSelectedSellingPlan || nameSelectedSellingPlan || defaultSellingPlan || availableSellingPlans[0]

          const isAnyPlanSelected = urlSelectedSellingPlan || nameSelectedSellingPlan || defaultSellingPlan

          group.selected = !!isAnyPlanSelected
        })

        if ((!this.variantChanged && this.options?.start_onetime == false) || this.product.requires_selling_plan == true || urlSelectedSellingPlanId || this.selectedSellingPlan) {
          let selectedSellingPlanGroup = this.availableSellingPlanGroups.find(group => group.selected) || this.availableSellingPlanGroups[0]
          let selectedSellingPlanId = selectedSellingPlanGroup.selected_selling_plan.id
          this.selectSellingPlan(selectedSellingPlanId)
        } else {
          this.selectSellingPlanGroup(null)
        }
      } else {
        this.selectSellingPlanGroup(null)
      }
    }

    if (changed.has('selectedSellingPlan')) {
      //dispatch CustomEvent to tell that this specific plan picker was updated, and pass the selectedSellingPlan
      const event = new CustomEvent(`skio::update-selling-plan`, {
        bubbles: true,
        composed: true,
        detail: {
          variant: this.selectedVariant,
          sellingPlan: this.selectedSellingPlan,
          key: this.key,
        },
      })

      this.dispatchEvent(event)

      this.dispatchEvent(
        new CustomEvent('change', {
          detail: { value: this.selectedSellingPlan?.id },
        })
      )
    }

    this.updateForm()
    this.updateExternalPrice()

    if (!this.disableUrl) {
      this.updateURLParams()
    }

    if (changed.has('quantity')) {
      this.getRule()
    }

    if (changed.has('cart')) {
      this.getRule()
    }

    this.updateExternalElements()
    this.updateTooltipContent()
  }

  updateExternalElements() {
    document.querySelectorAll(`[skio-price][skio-key="${this.key}"]`).forEach(el => {
      el.innerHTML = this.price(this.selectedSellingPlan)
    })

    document.querySelectorAll(`[skio-subscription-content]`).forEach(el => {
      el.style.display = this.selectedSellingPlan ? 'block' : 'none'
    })
  }

  // SECTION: Utility Functions
  log(...args) {
    args.unshift(`%c[skio plan picker][${this.key}]`, 'color: #8770f2;')
    console.log.apply(console, args)
  }

  error(...args) {
    args.unshift(`%c [skio plan picker][${this.key}]`, 'color: #ff0000')
    console.error.apply(console, args)
  }

  appendOrdinalSuffix(num) {
    const j = num % 10,
      k = num % 100
    if (j == 1 && k != 11) {
      return num + 'st'
    }
    if (j == 2 && k != 12) {
      return num + 'nd'
    }
    if (j == 3 && k != 13) {
      return num + 'rd'
    }
    return num + 'th'
  }

  viable() {
    const errors = []

    const sellingPlanInput = this.querySelector('input[name=selling_plan]')
    if (!sellingPlanInput) {
      errors.push('Missing selling plan input')
    }

    if (!this.product) {
      errors.push('No product found. Please pass a product or productHandle to the skio-plan-picker component.')
    }
    if (!this.selectedVariant) {
      errors.push('No variant found. Please pass a product or productHandle to the skio-plan-picker component.')
    }
    if (!this.form) {
      errors.push('No form found. Please add a form_id to the plan picker through the Shopify customizer or ensure that the element is in a form that has an input with name="id".')
    }
    if (!this.variantInput) {
      errors.push('No variant input found. Please add a form_id to the plan picker through the Shopify customizer or ensure that the element is in a form that has an input with name="id"')
    }

    if (errors.length == 0) return true

    this.error('Errors', errors)
    return false
  }

  getAvailableSellingPlans(group) {
    let availableSellingPlans = group.selling_plans.filter(selling_plan =>
      this.selectedVariant.selling_plan_allocations.some(selling_plan_allocation => selling_plan_allocation.selling_plan_id === selling_plan.id)
    )

    return availableSellingPlans
  }

  // ===================================================================
  // EUREKA — identity-safe group comparison.
  //
  // updated() rebuilds `availableSellingPlanGroups` (and, when
  // options.combine_groups is on, creates a BRAND NEW group object) each
  // time selectedVariant changes. `selectedSellingPlanGroup` therefore
  // often holds a reference to an object that is no longer the same
  // instance as the one being rendered — even though it is logically the
  // same group.
  //
  // Comparing with `==` then returns false on the FIRST click after load,
  // so handleContainerClick() re-selects instead of toggling off. Comparing
  // by `id` (with a name fallback for the synthetic combined group) removes
  // the reference dependency entirely and fixes the first-click bug.
  // ===================================================================
  isGroupSelected(group) {
    if (!group || !this.selectedSellingPlanGroup) return false
    if (group.id != null && this.selectedSellingPlanGroup.id != null) {
      return group.id === this.selectedSellingPlanGroup.id
    }
    return group.name === this.selectedSellingPlanGroup.name
  }

  // ===================================================================
  // EUREKA — parse the selected variant's weight in GRAMS from its TITLE.
  //
  // variant.weight is unreliable on this store, so we read the size out of
  // the variant title instead. Checks 'kg' BEFORE 'g' so "1.8kg" is not
  // mis-read as grams. Returns grams (Number) or null if nothing parseable.
  //
  // MULTI-PACK: a leading quantity like "3x 800g" / "3 x 800g" means the
  // TOTAL weight is qty * per-bag weight. We detect that "<n>x" multiplier
  // and multiply the parsed per-unit weight by it, so a 3x 800g pack reads
  // as 2400g (>= 1800 -> Free Welcome Pack), not 800g.
  //
  //   "1.8kg"          -> 1800
  //   "800g"           -> 800
  //   "3 KG"           -> 3000
  //   "Starter Pack - 3x 800g" -> 2400
  //   "2 x 1.5kg"      -> 3000
  // ===================================================================
  parseVariantWeightGrams(variant = this.selectedVariant) {
    const title = (variant?.title || variant?.public_title || '').toLowerCase()
    if (!title) return null

    // Leading pack multiplier, e.g. "3x", "3 x", "3 X 800g".
    // Only treat it as a multiplier when an "x" follows the number, so a
    // plain "800g" is never affected.
    const multMatch = title.match(/(\d+(?:\.\d+)?)\s*x\b/)
    const multiplier = multMatch ? parseFloat(multMatch[1]) : 1

    // kg first (so the 'g' inside 'kg' doesn't match the grams branch)
    const kg = title.match(/(\d+(?:\.\d+)?)\s*kg/)
    if (kg) return Math.round(parseFloat(kg[1]) * 1000 * multiplier)

    const g = title.match(/(\d+(?:\.\d+)?)\s*g/)
    if (g) return Math.round(parseFloat(g[1]) * multiplier)

    return null
  }

  // ===================================================================
  // EUREKA — which of the TWO weight-conditional gift rows to show.
  //
  // This ONLY governs the Free Welcome Pack / Free Welcome Treat pair.
  // "Free Treats for Life" is NOT handled here — it is a permanent row
  // that is always appended in additionalContentText() regardless.
  //
  // Returns an array containing 'pack' and/or 'treat':
  //
  //   • One-time selected (selectedSellingPlanGroup == null):
  //         BOTH  ->  ['pack', 'treat']
  //   • Autoship selected: ONE, by selected-variant weight —
  //         >= threshold  ->  ['pack']
  //         <  threshold  ->  ['treat']
  //     If weight can't be parsed, fall back to BOTH so the customer
  //     never loses sight of the perk.
  //
  // Threshold comes from the editor (this.options.ep_treat_threshold_g),
  // defaulting to 1800 g (1.8 kg) when unset.
  // ===================================================================
  benefitRowsToShow() {
    const EP_TREAT_THRESHOLD_G = Number(this.options?.ep_treat_threshold_g) || 1800

    // Weight decides the row for BOTH Autoship and Buy One Time.
    const grams = this.parseVariantWeightGrams()
    if (grams == null) return ['pack', 'treat'] // safe fallback

    return grams >= EP_TREAT_THRESHOLD_G ? ['pack'] : ['treat']
  }

  // !SECTION: Utility Functions

  // Update selected selling plan group; called on click of group-container element
  selectSellingPlanGroup(group) {
    this.selectedSellingPlanGroup = group
    this.selectedSellingPlan = group?.selected_selling_plan
  }

  // Update selected selling plan; called on change of skio-frequency select element
  selectSellingPlan(selling_plan_id) {
    const selectedGroup = this.availableSellingPlanGroups.find(group => group.selling_plans.some(plan => plan.id == selling_plan_id))
    const selectedSellingPlan = selectedGroup.selling_plans.find(plan => plan.id == selling_plan_id)

    selectedGroup.selected_selling_plan = selectedSellingPlan
    this.selectedSellingPlanGroup = selectedGroup
    this.selectedSellingPlan = selectedSellingPlan
  }

  // ===================================================================
  // EUREKA — Apple-style toggle behaviour for the option rows.
  //
  // The two rows (Autoship / Buy One Time) are mutually exclusive but
  // presented as toggle switches. Clicking the row that is already ON turns
  // it OFF and switches to the other row — like an iOS switch.
  //
  // Three guards keep this reliable:
  //  1. Clicks bubbling from the expanded content (.group-content — i.e. the
  //     frequency dropdown, benefit card and info icons) are ignored, so
  //     interacting with those never toggles the option off.
  //  2. A <label for="…"> dispatches a SECOND synthetic click onto its hidden
  //     radio (.group-input); that click also bubbles here. We ignore it so a
  //     single user click doesn't toggle twice.
  //  3. Group identity is compared via isGroupSelected() (by id, not object
  //     reference), because updated() replaces the group objects on every
  //     variant change.
  // ===================================================================
  handleContainerClick(e, group) {
    if (e.target.closest('.group-content') || e.target.classList?.contains('group-input')) return

    if (group == null) {
      // ONE-TIME row.
      if (this.selectedSellingPlanGroup == null && this.availableSellingPlanGroups?.length) {
        // Already one-time -> clicking again turns it off and switches to Autoship.
        this.selectSellingPlanGroup(this.availableSellingPlanGroups[0])
      } else {
        this.selectSellingPlanGroup(null)
      }
    } else {
      // AUTOSHIP row.
      if (this.isGroupSelected(group)) {
        // Already subscribed -> clicking again turns it off and switches to One-time.
        // Only allowed when a one-time option actually exists.
        if (!this.product.requires_selling_plan) {
          this.selectSellingPlanGroup(null)
        }
      } else {
        this.selectSellingPlanGroup(group)
      }
    }
  }

  updateExternalPrice() {
    document.querySelectorAll(this.options?.external_price_selector).forEach(el => {
      this.selectedSellingPlan ? (el.innerHTML = this.price(this.selectedSellingPlan)) : (el.innerHTML = this.money(this.selectedVariant.price))
    })
  }

  additionalFrequencyLabel() {
    if (!this.options?.additional_frequency_label || this.options?.additional_frequency_label == '') return

    return unsafeHTML(this.options?.additional_frequency_label)
  }

  // ===================================================================
  // EUREKA — benefit card + info popups.  (V4 — editor-driven)
  //
  // All copy, prices and images now come from block settings (Shopify
  // theme editor), delivered via:
  //   • this.options.<id>            — text / price / richtext fields
  //                                    (ride the options='...' JSON attr)
  //   • getAttribute('data-ep-*')    — resolved image URLs (image_picker
  //                                    settings only expose an ID, so the
  //                                    Liquid block resolves them to URLs
  //                                    and passes them as data-attributes)
  // Each read falls back to the previous hardcoded value when the setting
  // is empty, so the widget still renders correctly before anything is set.
  //
  // ROW STRUCTURE (unchanged):
  //   Row A — weight-conditional PAIR (Pack / Treat) via benefitRowsToShow()
  //   Row B — "Free Treats for Life", PERMANENT, always appended
  //
  // Popups (ep-popup-welcome / -treats / -life) move to document.body on
  // first open; their CSS is injected into <head> once by injectPopupStyles().
  // ===================================================================
  additionalContentText() {
    const o = this.options || {}
    const rows = this.benefitRowsToShow()

    // Escape plain-text values so a stray < or & can't break the markup.
    // (Rich-text fields are trusted admin HTML and are inserted as-is.)
    const esc = s =>
      String(s == null ? '' : s)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')

    const attr = (name, fallback) => {
      const v = this.getAttribute(name)
      return v && v.trim() !== '' ? v : fallback
    }
    const val = (v, fallback) => (v != null && String(v).trim() !== '' ? v : fallback)

    // ---- Card header ----
    const cardHead = esc(val(o.ep_cardhead, 'Commitment Free: modify and cancel, anytime.'))

    // ---- Row values (setting -> fallback) ----
    const packTitle = esc(val(o.ep_pack_title, 'Free Welcome Pack'))
    const packPrice = esc(val(o.ep_pack_price, '$58.39'))
    const packFree = esc(val(o.ep_pack_free_label, 'Free'))
    const packThumb = attr('data-ep-pack-thumb', 'https://cdn.shopify.com/s/files/1/0768/0376/8604/files/b23685ef-9d7b-4c8c-b0eb-169227a1722d_430x.png?v=1784096500#')

    const treatTitle = esc(val(o.ep_treat_title, 'Free Welcome Treat'))
    const treatPrice = esc(val(o.ep_treat_price, '$15.50'))
    const treatFree = esc(val(o.ep_treat_free_label, 'Free'))
    const treatThumb = attr('data-ep-treat-thumb', 'https://cdn.shopify.com/s/files/1/0768/0376/8604/files/6f7ffd45-9749-4ff4-bd37-f4934c10d691_430x.png?v=1784096520#')

    const lifeTitle = esc(val(o.ep_life_title, 'Free Treats for Life'))
    const lifePrice = esc(val(o.ep_life_price, '$19.95 ea.'))
    const lifeFree = esc(val(o.ep_life_free_label, 'Free'))
    const lifeThumb = attr('data-ep-life-thumb', 'https://cdn.shopify.com/s/files/1/0768/0376/8604/files/0b7e6fd7-4b6e-40ea-aa79-22cbeeb301d5_430x.png?v=1784096532#')

    // ---- Popup values ----
    // Title split on first comma/period into two stacked lines.
    const splitTitle = t => {
      const m = String(t).match(/^(.*?[,.])\s*(.*)$/)
      return m && m[2] ? `${esc(m[1])}<br>${esc(m[2])}` : esc(t)
    }
    const placeholderImg = fill =>
      `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='430' height='520'%3E%3Crect width='430' height='520' fill='${fill}'/%3E%3C/svg%3E`

    const packPopEyebrow = esc(val(o.ep_pack_pop_eyebrow, 'Subscriber Perk!'))
    const packPopTitle = splitTitle(val(o.ep_pack_pop_title, 'Free Welcome Pack. On the house.'))
    const packPopBody = val(o.ep_pack_pop_body, `<p>Subscribe to any Eureka air-dried recipe and your very first delivery arrives with a complimentary welcome pack.</p>`)
    const packPopImage = attr('data-ep-pack-pop-image', placeholderImg('%23E9E3D7'))
    const packPopTc = val(o.ep_pack_pop_tc, 'Free Welcome Pack cannot be substituted. <a href="#">Full T&amp;Cs.</a>')

    const treatPopEyebrow = esc(val(o.ep_treat_pop_eyebrow, 'Subscriber Perk!'))
    const treatPopTitle = splitTitle(val(o.ep_treat_pop_title, 'Free welcome treat. On the house.'))
    const treatPopBody = val(o.ep_treat_pop_body, `<p>Subscribe to any Eureka air-dried recipe and we'll throw in a free pack of treats with your first delivery.</p>`)
    const treatPopImage = attr('data-ep-treat-pop-image', placeholderImg('%23E3E0DA'))
    const treatPopTc = val(o.ep_treat_pop_tc, 'Free Treat recipes cannot be substituted. <a href="#">Full T&amp;Cs.</a>')

    const lifePopEyebrow = esc(val(o.ep_life_pop_eyebrow, 'Subscriber Perk!'))
    const lifePopTitle = splitTitle(val(o.ep_life_pop_title, 'Free treats for life. Every order.'))
    const lifePopBody = val(o.ep_life_pop_body, `<p>Subscribe to any Eureka air-dried recipe and we'll throw in a free pack of treats with every recurring delivery.</p>`)
    const lifePopImage = attr('data-ep-life-pop-image', placeholderImg('%23E3E0DA'))
    const lifePopTc = val(o.ep_life_pop_tc, 'Free Treat recipes cannot be substituted. <a href="#">Full T&amp;Cs.</a>')

    // ---- Conditional row: FREE WELCOME PACK ----
    const packRow = `
        <div class="ep-sub-row ep-sub-row-welcom-p">
          <img class="ep-sub-thumb" src="${packThumb}" alt="${packTitle}" loading="lazy" />
          <span class="ep-sub-name">
            ${packTitle}
            <span class="ep-sub-info" role="button" tabindex="0" aria-label="About ${packTitle}" onclick="var i='ep-popup-welcome';var p=document.getElementById(i)||this.getRootNode().querySelector('#'+i);if(p){if(p.parentNode!==document.body)document.body.appendChild(p);p.style.display='flex';document.documentElement.style.overflow='hidden';}" onkeydown="if(event.key==='Enter'||event.key===' '){event.preventDefault();this.click();}">
              <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" role="presentation"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="16" x2="12" y2="12"></line><line x1="12" y1="8" x2="12.01" y2="8"></line></svg>
            </span>
          </span>
          <span class="ep-sub-price"><s>${packPrice}</s><span class="ep-sub-free">${packFree}</span></span>
        </div>
    `

    // ---- Conditional row: FREE WELCOME TREAT ----
    const treatRow = `
        <div class="ep-sub-row ep-sub-row-welcome-t">
          <img class="ep-sub-thumb" src="${treatThumb}" alt="${treatTitle}" loading="lazy" />
          <span class="ep-sub-name">
            ${treatTitle}
            <span class="ep-sub-info" role="button" tabindex="0" aria-label="About ${treatTitle}" onclick="var i='ep-popup-treats';var p=document.getElementById(i)||this.getRootNode().querySelector('#'+i);if(p){if(p.parentNode!==document.body)document.body.appendChild(p);p.style.display='flex';document.documentElement.style.overflow='hidden';}" onkeydown="if(event.key==='Enter'||event.key===' '){event.preventDefault();this.click();}">
              <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" role="presentation"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="16" x2="12" y2="12"></line><line x1="12" y1="8" x2="12.01" y2="8"></line></svg>
            </span>
          </span>
          <span class="ep-sub-price"><s>${treatPrice}</s><span class="ep-sub-free">${treatFree}</span></span>
        </div>
    `

    // ---- PERMANENT row: FREE TREATS FOR LIFE (always shown) ----
    const lifeRow = `
        <div class="ep-sub-row">
          <img class="ep-sub-thumb" src="${lifeThumb}" alt="${lifeTitle}" loading="lazy" />
          <span class="ep-sub-name">
            ${lifeTitle}
            <span class="ep-sub-info" role="button" tabindex="0" aria-label="About ${lifeTitle}" onclick="var i='ep-popup-life';var p=document.getElementById(i)||this.getRootNode().querySelector('#'+i);if(p){if(p.parentNode!==document.body)document.body.appendChild(p);p.style.display='flex';document.documentElement.style.overflow='hidden';}" onkeydown="if(event.key==='Enter'||event.key===' '){event.preventDefault();this.click();}">
              <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" role="presentation"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="16" x2="12" y2="12"></line><line x1="12" y1="8" x2="12.01" y2="8"></line></svg>
            </span>
          </span>
          <span class="ep-sub-price"><s>${lifePrice}</s><span class="ep-sub-free">${lifeFree}</span></span>
        </div>
    `

    // Conditional pack/treat first (pack then treat), then the permanent
    // "Free Treats for Life" row appended unconditionally.
    const rowsHtml = (rows.includes('pack') ? packRow : '') + (rows.includes('treat') ? treatRow : '') + lifeRow

    const benefitHtml = `
      <!-- ============================ BENEFIT CARD ============================ -->
      <!-- ============================ BENEFIT CARD ============================ -->
      <div class="ep-sub-card ${this.selectedSellingPlanGroup == null ? 'ep-sub-card--onetime' : 'ep-sub-card--sub'}">
        <span class="ep-sub-cardhead">${cardHead}</span>
        ${rowsHtml}
      </div>

      <!-- ========================= GUARANTEE BOX (hidden by default) =========================
           Add the class "ep-sub-guarantee--show" to the div below to display it.
      -->
      <div class="ep-sub-guarantee">
        <img class="ep_sb_guar" src="https://cdn.shopify.com/s/files/1/0768/0376/8604/files/81e47c2b-6933-4343-a6f8-b51e1fc53987.svg?v=1782816730" alt="" />
        <div class="ep-sub-guarantee__text">
          <span class="ep-sub-guarantee__title">Risk Free: 100% Money Back Guarantee</span>
          <span class="ep-sub-guarantee__sub">No Commitment - Cancel or Change, anytime</span>
        </div>
      </div>


      <!-- POPUP 1 — FREE WELCOME PACK (id: ep-popup-welcome) -->
      <div id="ep-popup-welcome" class="ep-popup" onclick="if(event.target===this){this.style.display='none';document.documentElement.style.overflow='';}">
        <div class="ep-popup__dialog">
          <button type="button" class="ep-popup__close" aria-label="Close" onclick="this.closest('.ep-popup').style.display='none';document.documentElement.style.overflow='';">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M6 6l12 12M18 6L6 18"/></svg>
          </button>
          <img class="ep-popup__img" src="${packPopImage}" alt="${packTitle}" />
          <div class="ep-popup__content">
            <div class="ep-popup__eyebrow">${packPopEyebrow}</div>
            <div class="ep-popup__title">${packPopTitle}</div>
            <div class="ep-popup__body">${packPopBody}</div>
            <div class="ep-popup__tc">${packPopTc}</div>
          </div>
        </div>
      </div>


      <!-- POPUP 2 — FREE WELCOME TREAT (id: ep-popup-treats) -->
      <div id="ep-popup-treats" class="ep-popup" onclick="if(event.target===this){this.style.display='none';document.documentElement.style.overflow='';}">
        <div class="ep-popup__dialog">
          <button type="button" class="ep-popup__close" aria-label="Close" onclick="this.closest('.ep-popup').style.display='none';document.documentElement.style.overflow='';">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M6 6l12 12M18 6L6 18"/></svg>
          </button>
          <img class="ep-popup__img" src="${treatPopImage}" alt="${treatTitle}" />
          <div class="ep-popup__content">
            <div class="ep-popup__eyebrow">${treatPopEyebrow}</div>
            <div class="ep-popup__title">${treatPopTitle}</div>
            <div class="ep-popup__body">${treatPopBody}</div>
            <div class="ep-popup__tc">${treatPopTc}</div>
          </div>
        </div>
      </div>


      <!-- POPUP 3 — FREE TREATS FOR LIFE (id: ep-popup-life) -->
      <div id="ep-popup-life" class="ep-popup" onclick="if(event.target===this){this.style.display='none';document.documentElement.style.overflow='';}">
        <div class="ep-popup__dialog">
          <button type="button" class="ep-popup__close" aria-label="Close" onclick="this.closest('.ep-popup').style.display='none';document.documentElement.style.overflow='';">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M6 6l12 12M18 6L6 18"/></svg>
          </button>
          <img class="ep-popup__img" src="${lifePopImage}" alt="${lifeTitle}" />
          <div class="ep-popup__content">
            <div class="ep-popup__eyebrow">${lifePopEyebrow}</div>
            <div class="ep-popup__title">${lifePopTitle}</div>
            <div class="ep-popup__body">${lifePopBody}</div>
            <div class="ep-popup__tc">${lifePopTc}</div>
          </div>
        </div>
      </div>
    `

    return html` <div class="group-content-additional">${unsafeHTML(benefitHtml)}</div> `
  }

  // SECTION: Discount Functions
  // Calculates discount based on selling_plan.price_adjustments, returns { percent, amount } of selling plan discount
  discountText(selling_plan) {
    if (!this.options?.discount_text || this.options?.discount_text == '') return
    const discount = this.discount(selling_plan)
    const hasInvalidDiscount = Object.values(discount).some(value => value === 0 || value === Infinity || value.toString().includes('-'))

    if (hasInvalidDiscount) {
      return '' // Return empty if any invalid condition is met
    } else {
      return unsafeHTML(
        this.options?.discount_text
          .replaceAll('[discount]', this.options?.discount_format === 'absolute' ? this.money(discount.absolute) : discount.percent + '%')
          .replaceAll('[future_price_adjustments]', this.postCheckoutDiscountsText(selling_plan) || '')
      )
    }
  }

  discount(selling_plan) {
    if (!selling_plan) return { percent: 0, amount: 0 }
    return this.getDiscountFromPriceAdjustment(selling_plan, selling_plan.price_adjustments[0])
  }

  postCheckoutPriceAdjustments(selling_plan) {
    const { price_adjustments } = selling_plan
    const postCheckoutPriceAdjustments = price_adjustments.filter(({ position }) => position !== 1)
    const discounts = postCheckoutPriceAdjustments.map(price_adjustment => this.getDiscountFromPriceAdjustment(selling_plan, price_adjustment))
    return discounts
  }

  postCheckoutDiscountsText(sellingPlan = this.selectedSellingPlan) {
    if (!sellingPlan || !this.options?.future_price_adjustments_text || this.future_price_adjustments_text == '') return

    const postCheckoutPriceAdjustments = this.postCheckoutPriceAdjustments(sellingPlan)
    if (!postCheckoutPriceAdjustments.length) return

    return postCheckoutPriceAdjustments.map((discount, index) =>
      this.options?.future_price_adjustments_text
        .replaceAll('[discount]', this.options?.discount_format === 'absolute' ? this.money(discount.absolute) : discount.percent + '%')
        .replaceAll('[order_count]', index + 1)
        .replaceAll('[order_count_ordinal]', this.appendOrdinalSuffix(index + 1))
    )
  }

  getDiscountFromPriceAdjustment(selling_plan, price_adjustment) {
    const discount = {
      percent: 0,
      amount: 0,
      absolute: 0,
    }

    let multiplier = 1

    let sellingPlanGroup = this.product.selling_plan_groups.find(group => group.selling_plans.some(plan => plan.id == selling_plan.id))

    if (sellingPlanGroup?.name === 'Prepaid') {
      const str = selling_plan.name // replace with your string
      const intervalDate = /\b\d+\s*(days|weeks|months|years)\b/gi
      const intervalDateMatches = str.match(intervalDate)

      if (intervalDateMatches?.length) {
        const intervals = intervalDateMatches[0].match(/\d+/g)
        multiplier = intervals[0] / (intervals[1] || 1)
        multiplier = multiplier > 1 ? multiplier : 1
      }
    }

    const price = this.selectedVariant.price
    const compareAtPrice = this.selectedVariant.compare_at_price && this.selectedVariant.compare_at_price > price ? this.selectedVariant.compare_at_price : price

    switch (price_adjustment.value_type) {
      case 'percentage':
        discount.percent = price_adjustment.value
        discount.absolute = Math.round((price * price_adjustment.value) / 100.0)
        discount.amount = Math.round((price * price_adjustment.value) / 100.0)
        break
      case 'fixed_amount':
        discount.percent = Math.round(((price_adjustment.value * 1.0) / price) * 100.0)
        discount.absolute = price_adjustment.value
        discount.amount = price_adjustment.value
        break
      case 'price':
        discount.percent = Math.round((((compareAtPrice * multiplier - price_adjustment.value) * 1.0) / (compareAtPrice * multiplier)) * 100.0)
        discount.absolute = compareAtPrice * multiplier - price_adjustment.value
        discount.amount = price - price_adjustment.value
        break
    }

    return discount
  }

  /**
   * Formats a number of cents into a currency string using Shopify format string.
   *
   * https://help.shopify.com/en/manual/international/pricing/currency-formatting#currency-formatting-options
   *
   * Originally from: https://gist.github.com/stewartknapman/8d8733ea58d2314c373e94114472d44c
   *
   * @param cents - The number of cents to format.
   * @param formatString - The format string to use.
   * @returns The formatted currency string.
   */
  money(cents, formatString = this.moneyFormat) {
    const placeholderRegex = /{{\s*(\w+)\s*}}/

    /**
     * Formats a number of cents into a currency string using the provided
     * precision, thousands separator, and decimal separator.
     */
    function formatWithDelimiters(number, precision = 2, thousands = ',', decimal = '.') {
      if (isNaN(number) || number == null) {
        return '0'
      }

      const numString = (number / 100.0).toFixed(precision)
      const parts = numString.split('.')
      const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands)
      const cents = parts[1] ? decimal + parts[1] : ''
      return dollars + cents
    }

    return formatString.replace(placeholderRegex, (match, placeholder) => {
      switch (placeholder) {
        case 'amount':
          // Ex.	1,134.65
          return formatWithDelimiters(cents, 2)
        case 'amount_no_decimals':
          // Ex. 1,135
          return formatWithDelimiters(cents, 0)
        case 'amount_with_comma_separator':
          // Ex. 1.134,65
          return formatWithDelimiters(cents, 2, '.', ',')
        case 'amount_no_decimals_with_comma_separator':
          // Ex. 1.135
          return formatWithDelimiters(cents, 0, '.', ',')
        case 'amount_with_apostrophe_separator':
          // Ex. 1'134.65
          return formatWithDelimiters(cents, 2, "'", '.')
        case 'amount_no_decimals_with_space_separator':
          // Ex. 1 135
          return formatWithDelimiters(cents, 0, ' ')
        case 'amount_with_space_separator':
          // 1 134,65
          return formatWithDelimiters(cents, 2, ' ', ',')
        case 'amount_with_period_and_space_separator':
          // 1 134.65
          return formatWithDelimiters(cents, 2, ' ', '.')
        default:
          return match
      }
    })
  }

  // !SECTION: Discount Functions
  price(selling_plan, formatted = true) {
    if (this.rule) {
      return formatted
        ? this.money(this.selectedVariant.price - this.discount(selling_plan).amount - this.selectedVariant.price * (this.rule.discountAmount / 100))
        : this.selectedVariant.price - this.discount(selling_plan).amount - this.selectedVariant.price * (this.rule.discountAmount / 100)
    }

    return formatted ? this.money(this.selectedVariant.price - this.discount(selling_plan).amount) : this.selectedVariant.price - this.discount(selling_plan).amount
  }

  unitPriceTemplate(selling_plan = null) {
    const unitPrice = this.selectedVariant?.unit_price
    const measurement = this.selectedVariant?.unit_price_measurement
    if (!unitPrice || !measurement) return ''

    let displayPrice = unitPrice
    if (selling_plan) {
      const subscriptionPrice = this.price(selling_plan, false)
      displayPrice = Math.round((unitPrice * subscriptionPrice) / this.selectedVariant.price)
    }

    return html`<div class="skio-unit-price">${this.money(displayPrice)}/${measurement.reference_unit}</div>`
  }

  // Updates element data to be registered by forms
  updateForm() {
    let $sellingPlan = this.querySelector('input[name=selling_plan]')

    if ($sellingPlan) {
      $sellingPlan.value = this.selectedSellingPlan ? this.selectedSellingPlan.id : ''
      $sellingPlan.dispatchEvent(new Event('change'))
    }
  }

  // SECTION: Additional Functionality
  updateURLParams() {
    const url = new URL(window.location.href)

    if (this.selectedSellingPlan) {
      url.searchParams.set('selling_plan', this.selectedSellingPlan.id)
      window.history.replaceState({}, '', url.href)
    } else {
      url.searchParams.delete('selling_plan')
      window.history.replaceState({}, '', url.href)
    }
  }

  // Runs a fetch request to add the selectedVariant to the cart with the passed quantity and selectedSellingPlan
  addToCart(quantity) {
    const items = [
      {
        id: this.selectedVariant.id,
        quantity: quantity,
        ...(this.selectedSellingPlan && {
          selling_plan: this.selectedSellingPlan?.id,
        }),
      },
    ]

    fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        items,
      }),
    })
      .then(response => response.json())
      .then(response => {
        this.log('Added item to cart: ', response)
        //dispatch CustomEvent to tell document that an item was added to cart
        const event = new CustomEvent(`skio::added-to-cart`, {
          bubbles: true,
          composed: true,
          detail: {
            response,
            key: this.key,
          },
        })

        this.dispatchEvent(event)
      })
      .catch(error => {
        this.error(`SKIO ${this.key} error adding item to cart: `, error)
      })
  }

  fetchProduct = handle => {
    return fetch(`/products/${handle}.js`)
      .then(response => response.json())
      .then(product => {
        this.product = product
        this.selectedVariant = product.variants[0]

        return product
      })
  }

  replacePlaceholders(template, sellingPlan = this.selectedSellingPlan) {
    if (!template) return ''

    const discount = this.discount(sellingPlan)
    const discountText = this.options?.discount_format === 'absolute' ? this.money(discount.absolute) : discount.percent + '%'

    const futureDiscounts = this.postCheckoutDiscountsText(sellingPlan) || []
    const futureDiscountsText = futureDiscounts.join(', ')

    return template.replaceAll('[discount]', discountText).replaceAll('[future_price_adjustments]', futureDiscountsText)
  }

  updateTooltipContent() {
    const template = this.options?.tooltip_content || ''
    let plan = this.selectedSellingPlan
    if (!plan && this.availableSellingPlanGroups?.length) {
      const firstGroup = this.availableSellingPlanGroups[0]
      plan = firstGroup.selected_selling_plan || firstGroup.selling_plans?.[0] || null
    }
    this.tooltipContent = this.replacePlaceholders(template, plan)
  }

  // !SECTION: Additional Functionality
}

if (!customElements.get('skio-plan-picker')) {
  customElements.define('skio-plan-picker', SkioPlanPicker)
}

if (window) window.SkioPlanPicker = SkioPlanPicker